import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { FaStar, FaRegStar } from "react-icons/fa";

import {
  BRAND_TAGLINE,
  BRAND_SLOGAN,
  BRAND_COLOR_GRADIENT_ONE,
  BRAND_COLOR_GRADIENT_TWO,
} from "../../../common/constants";

import heroImage from "../../../assets/hero-speaking.png";
import { fetchReviewSummary } from "../../services/reviewService";

import "./heroSection.css";

const HeroSection = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const res = await fetchReviewSummary();
      setAverageRating(res.data?.averageRating || 0);
      setTotalReviews(res.data?.totalReviews || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const handleHowItWorksClick = () => {
    if (location.pathname === "/") {
      document
        .getElementById("how-it-works")
        ?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollToHowItWorks: true } });
    }
  };

  return (
    <section
      className="hero-section"
      style={{
        background: `linear-gradient(to right, ${BRAND_COLOR_GRADIENT_ONE}, ${BRAND_COLOR_GRADIENT_TWO})`,
      }}
    >
      <div className="hero-container">

        {/* LEFT */}
        <motion.div
          initial={{ x: -40 }}    
          animate={{ x: 0 }}   
          transition={{ duration: 0.8 }}
        >
          <h1 className="hero-title">{BRAND_TAGLINE}</h1>

          <p className="hero-subtitle">
            {BRAND_SLOGAN}
            <br />
            Build confidence and express yourself freely in real conversations.
          </p>

          {/* RATING */}
          <motion.div
            className="rating-summary"
            initial={{ y: 16 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="stars">
              {[1, 2, 3, 4, 5].map((i) =>
                i <= Math.round(averageRating) ? (
                  <FaStar key={i} size={16} color="#FFD700" />
                ) : (
                  <FaRegStar key={i} size={16} color="#FFD700" />
                )
              )}
            </div>

            <span className="rating-strong">
              {loading ? "—" : averageRating.toFixed(1)}
            </span>
            <span className="rating-text">out of 5</span>
            <span className="rating-text">
              ({loading ? "—" : totalReviews} reviews)
            </span>
            <span
              className="show-reviews"
              onClick={() => navigate("/reviews")}
            >
              Show reviews
            </span>
          </motion.div>

          {/* BUTTONS */}
          <div className="hero-buttons">
            <button
              className="primary-btn-live-session"
              style={{
                color: BRAND_COLOR_GRADIENT_TWO,
                border: `2px solid ${BRAND_COLOR_GRADIENT_TWO}`,
              }}
              onClick={() => navigate("/timetable")}
            >
              Book a Live Session
            </button>

            <button
              className="secondary-btn"
              onClick={handleHowItWorksClick}
            >
              How It Works
            </button>
          </div>
        </motion.div>

        {/* RIGHT IMAGE */}
        <motion.div
          className="hero-image-wrapper"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <img
            src={heroImage}
            alt="Speak confidently online"
            className="hero-image"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
